import { CancelButton, SaveButton } from '@core/components/Buttons';
import { SimpleDialog, SimpleDialogContent } from '@core/components/Dialog';
import ImportErrorTable from '@core/components/ImportModal/ImportErrorTable';
import ImportModal from '@core/components/ImportModal/ImportModal';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { downloadFile } from '@core/util/downloadUtils';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import FuseUtils from '@fuse/utils/FuseUtils';
import { FornecedorActions } from 'app/main/fornecedor/store';
import { useFornecedor } from 'app/main/fornecedor/store/fornecedorContext';
import fornecedorApi from 'app/services/api/fornecedorApi';
import * as Actions from 'app/store/actions';
import { eventReactGA } from 'app/utils/analyticsUtil';
import { FormikProvider, useFormik } from 'formik';
import React, { useState } from 'react';
import { FaTruck } from 'react-icons/fa';
import { IoIosAlert } from 'react-icons/io';
import * as yup from 'yup';

const FORNECEDOR = 'Fornecedor';

const ImportarFornecedorModal = () => {
  const [state, dispatch] = useFornecedor();
  const apiUtils = useApiUtils(dispatch, Actions);
  const { abrirImportModal, fileImport } = state;
  const [errorData, setErrorData] = useState([]);
  const [error, setError] = useState(false);
  const [idNotificacao, setIdNotificacao] = useState();
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [progress, setProgress] = useState(0);

  const extensoesPermitidas = ['xlsx'];

  useReduxLoading(loading);

  const titulo = error ? 'Erros identificados na importação' : 'Importar planilha de fornecedores';

  const obterModelo = async () => {
    try {
      setLoading(true);
      eventReactGA(FORNECEDOR, 'Obter planilha');

      const response = await fornecedorApi.obterModelo();
      downloadFile(response.data.bytes, response.data.nome);
    } catch (error) {
      apiUtils.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const resetFileSelection = () => {
    FornecedorActions.setFileImport(dispatch, '');
  };

  const handleCancel = () => {
    resetFileSelection();
    setDisabled(false);
    if (error) {
      setError(false);
      setErrorData([]);
    } else {
      closeImportModal();
      formik.resetForm(formik.initialValues);
    }
  };

  const closeImportModal = () => {
    FornecedorActions.fecharImportModal(dispatch);
    resetFileSelection();
  };

  const handleUpload = (file) => {
    if (fileImport) {
      try {
        const chaveNotificacao = FuseUtils.generateGUID();
        setIdNotificacao(chaveNotificacao);
        setDisabled(true);

        const arquivo = Object.assign(file, {
          id: undefined,
          nome: file.name,
          contentType: file.type,
        });

        const extensao = arquivo.nome.substring(arquivo.nome.lastIndexOf('.') + 1);

        if (!extensoesPermitidas.includes(extensao.toLowerCase())) {
          FornecedorActions.setFileImport(dispatch, '');
          dispatch(FornecedorActions.erro('É permitido anexar somente arquivos no formato .xlsx'));
          setLoading(false);
          setDisabled(false);
        } else {
          const formData = new FormData();
          formData.append('arquivo', arquivo);

          setLoading(true);
          eventReactGA(FORNECEDOR, 'Importar planilha');

          fornecedorApi.importarPlanilha(formData, chaveNotificacao).catch((error) => {
            apiUtils.handleApiError(error);
            setLoading(false);
            setDisabled(false);
          });
        }
      } catch (error) {
        apiUtils.handleApiError(error);
      }
    }
  };

  const handleSubmit = () => {
    setLoading(true);
    handleUpload(fileImport);
  };

  const ImportarArquivoSchema = yup.object().shape({
    arquivo: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('o arquivo')),
  });

  const getInitialValues = (registro) => ({
    ...registro,
    id: registro?.id,
    arquivo: registro?.arquivo,
  });

  const formik = useFormik({
    initialValues: getInitialValues(fileImport),
    validationSchema: ImportarArquivoSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const onFileChange = (event) => {
    const file = event.target.files[0];
    setProgress(0);

    formik.handleChange({
      target: {
        id: 'arquivo',
        value: file,
      },
    });

    FornecedorActions.setFileImport(dispatch, file);
  };

  const showMessage = (message, variant) => {
    dispatch(FornecedorActions.erro(message));
  };

  const handleProgressUpdate = (notificacao) => {
    if (idNotificacao === notificacao.id) {
      const mensagem = JSON.parse(notificacao.mensagem);
      const newProgress = parseFloat(mensagem.percentual);
      setProgress(newProgress);
      setLoading(false);

      if (newProgress > 0) {
        if (newProgress === 100) {
          if (mensagem?.errors && mensagem?.errors?.length > 0) {
            if (mensagem.errors[0].campo === 'arquivo-error') {
              showMessage(mensagem.errors[0].mensagemErro, 'error');
              setDisabled(false);
              setProgress(0);
            } else if (mensagem.errors[0].campo === 'arquivo-planilha-error') {
              showMessage(mensagem.errors[0].mensagemErro, 'error');
              setDisabled(false);
              setProgress(0);
            } else {
              setError(true);
              setErrorData(mensagem.errors);
            }
          }
          if (mensagem?.errors?.length === 0) {
            dispatch(FornecedorActions.sucesso('Importação realizada com sucesso.'));
            handleCancel();
            FornecedorActions.carregarRegistros(dispatch, state.requestParams, state.filtroAvancado);
          }
        }
      }
    }
  };

  return (
    <SimpleDialog open={abrirImportModal} onClose={handleCancel} innerScroll>
      <FormikProvider value={formik}>
        <SimpleDialogContent
          header={{
            title: titulo,
            icon: error ? <IoIosAlert /> : <FaTruck />,
            toolbar: (
              <>
                <CancelButton title="Cancelar" type="button" onClick={handleCancel}>
                  {error ? 'Fechar' : 'Cancelar'}
                </CancelButton>
                {!error && (
                  <SaveButton
                    id="importar-fornecedor"
                    type="submit"
                    onClick={() => formik.handleSubmit(formik.values)}
                    disabled={disabled}
                  >
                    Processar
                  </SaveButton>
                )}
              </>
            ),
          }}
          content={
            <>
              {error ? (
                <ImportErrorTable errors={errorData} />
              ) : (
                <ImportModal
                  onFileChange={onFileChange}
                  arquivo={fileImport}
                  obterModelo={obterModelo}
                  progress={progress}
                  handleProgressUpdate={handleProgressUpdate}
                  formik={formik}
                />
              )}
            </>
          }
          contentStyle={{ padding: 0 }}
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default ImportarFornecedorModal;
